import { useQueryClient } from '@tanstack/react-query';
import type { Editor } from '@tiptap/react';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { CollectionContextProvider } from '@/features/events/contexts/CollectionContext/CollectionContextProvider';
import type { UpdateEventMutation } from '@/features/events/hooks/useUpdateEvent.generated';
import { createDecoratedEvent } from '@/features/events/lib/event.lib';
import { useGetMagicSharePage } from '@/pages/QuickCreate/hooks/useGetMagicSharePage';
import { MagicJobStatus, type MagicJobType } from '@/types/gql.generated';
import { createExperienceEventsQueryKey } from '@/utils/queryKeys';
import { ChatMode } from '../../components/PromptChat/mode.lib';
import { useMultiExportStore } from '../../hooks/useMultiExportStore';
import { usePlatformExports } from '../../hooks/usePlatformExports';
import type {
  PromptSource,
  DecoratedMagicSharePage,
  DecoratedMagicJob,
} from '../../types';
import { useChatContext } from '../ChatContext/useChatContext';
import { useChatExperienceId } from '../ChatContext/useChatExperienceId';
import { useJobContext } from '../JobContext/useJobContext';
import { computeAppState } from './appState.lib';
import { useBase } from './hooks/useBase';
import { useInitialPromptType } from './hooks/useInitialPromptType';
import { usePrompt } from './hooks/usePrompt';
import { MagicContext } from './MagicContext';
import { MagicPromptContext } from './MagicPromptContext';
import type { PromptState, State } from './types';

type Props = {
  readOnly?: boolean;
  page?: DecoratedMagicSharePage | null;
  onReset?: () => void;
  // The chat was opened on an artifact that already exists, so the results
  // show it from the start rather than waiting on a first turn.
  hasArtifact?: boolean;
  children: ReactNode;
  promptType?: MagicJobType;
  initialPromptType?: MagicJobType;
};

export const MagicContextProvider = ({
  readOnly = false,
  page,
  onReset,
  hasArtifact = false,
  children,
  promptType: forcedPromptType,
  initialPromptType: overrideInitialPromptType,
}: Props) => {
  const queryClient = useQueryClient();
  const editorRef = useRef<Editor | null>(null);
  const {
    job,
    isCreating: isCreatingJob,
    reset: resetCreateJob,
    chatJobActivity,
    hasStartedChatJob,
    hasStartedChatTurn,
    hasPendingChatJob,
  } = useJobContext();
  const {
    chat,
    isFetching: isFetchingChat,
    reset: resetChat,
  } = useChatContext();
  const chatExperienceId = useChatExperienceId();
  const { reset: resetPlatformExports, invalidateEntryExports } =
    usePlatformExports();
  const [promptSource, setPromptSource] = useState<PromptSource>(null);
  const { promptType: initialPromptType, setPromptType } = useInitialPromptType(
    overrideInitialPromptType
  );
  const promptType = forcedPromptType ?? initialPromptType;
  const [instructions, setInstructions] = useState<string>('');
  const closeMultiExportCard = useMultiExportStore((store) => store.close);
  const { prompt, setPrompt: setPromptState, resetPrompt } = usePrompt();
  const { base, setBase } = useBase({
    job: job ?? undefined,
    chat,
    experience: undefined,
  });

  const baseRef = useRef(base);
  baseRef.current = base;

  // A job someone else owns (an emailed job opened from a forwarded link, or
  // by a second account) shows its results view-only. The server refuses the
  // edits anyway; this keeps the page from offering them.
  const isViewOnlyJob = base?.job?.canEdit === false;
  const isReadOnly = readOnly || isViewOnlyJob;

  const invalidateExperienceEvents = useCallback(() => {
    if (chatExperienceId) {
      queryClient.invalidateQueries({
        queryKey: createExperienceEventsQueryKey(chatExperienceId),
      });
    }
  }, [queryClient, chatExperienceId]);

  const onUpdate = useCallback(
    (result: UpdateEventMutation['updateEvent']): void => {
      const currentBase = baseRef.current;
      if (currentBase) {
        const updated = createDecoratedEvent(currentBase, result);
        invalidateEntryExports(updated);
      }
      invalidateExperienceEvents();
    },
    [invalidateEntryExports, invalidateExperienceEvents]
  );

  const [hasCompletedFirstJob, setHasCompletedFirstJob] = useState(
    job?.status === MagicJobStatus.Completed
  );

  useEffect(() => {
    if (!hasCompletedFirstJob && job?.status === MagicJobStatus.Completed) {
      setHasCompletedFirstJob(true);
    }
  }, [hasCompletedFirstJob, job]);

  const { slug: slugOrId } = useParams<{ slug: string }>();
  const { reset: resetMagicSharePage } = useGetMagicSharePage(slugOrId);

  const [mobileMode, setMobileMode] = useState(ChatMode.Results);

  const events = useMemo(
    () => base?.chat?.events ?? base?.job?.events ?? [],
    [base?.chat?.events, base?.job?.events]
  );

  const state = computeAppState({
    hasArtifact,
    isCreatingJob,
    hasCompletedFirstJob,
    isChatJobActive: chatJobActivity.isActive,
    isChatResponding: chatJobActivity.isResponding,
    hasStartedChatJob,
    hasStartedChatTurn,
    hasPendingChatJob,
    isFetchingChat,
    eventCount: events.length,
  });

  const setPrompt: PromptState['setPrompt'] = useCallback(
    (prompt, source = 'user-input') => {
      setPromptState(prompt);
      setPromptSource(source);
    },
    [setPromptState]
  );

  const setPromptFromJob = useCallback(
    (job: DecoratedMagicJob): void => {
      const textPrompt = job.sourceText ?? '';
      setPromptType(job.type);
      setPrompt({ text: textPrompt, html: textPrompt, file: job.file });
    },
    [setPromptType, setPrompt]
  );

  const resetAppState: State['resetAppState'] = useCallback(() => {
    setPromptSource(null);
    setBase(null);
    setHasCompletedFirstJob(false);
    resetPrompt();
    closeMultiExportCard();
    resetCreateJob();
    resetChat();
    resetPlatformExports();
    onReset?.();
    resetMagicSharePage();
    // Clearing the editor content only matters when the prompt editor is
    // mounted. On mobile the editor is unmounted while viewing results, and the
    // extension can reset without an editor in the tree at all — in both cases
    // the prompt-state reset above already clears the input, and the editor
    // re-initializes empty when it next mounts. A missing editor here is an
    // expected condition, not an error worth reporting (Sentry WEB-CLIENT-1J5).
    editorRef.current?.commands.setContent('');
    setMobileMode(ChatMode.Results);
  }, [
    setBase,
    resetPrompt,
    closeMultiExportCard,
    onReset,
    resetCreateJob,
    resetChat,
    resetPlatformExports,
    resetMagicSharePage,
  ]);

  const magicContext: State = useMemo(
    () => ({
      readOnly: isReadOnly,
      page,
      state,
      resetAppState,
      editorRef,
      mobileMode,
      promptType,
      promptSource,
      setMobileMode,
      setPromptType,
      setPromptSource,
    }),
    [
      isReadOnly,
      page,
      state,
      resetAppState,
      mobileMode,
      promptType,
      promptSource,
      setPromptType,
    ]
  );

  const promptContext: PromptState = useMemo(
    () => ({
      prompt,
      setPrompt,
      setPromptFromJob,
      instructions,
      setInstructions,
    }),
    [prompt, setPrompt, setPromptFromJob, instructions, setInstructions]
  );

  return (
    <MagicContext.Provider value={magicContext}>
      <MagicPromptContext.Provider value={promptContext}>
        <CollectionContextProvider
          base={base}
          events={events}
          setBase={setBase}
          onDelete={invalidateExperienceEvents}
          onUpdate={onUpdate}
        >
          {children}
        </CollectionContextProvider>
      </MagicPromptContext.Provider>
    </MagicContext.Provider>
  );
};
